/**
 * Converts the Workflows API wire types (`dto.ts`) to the builder's models
 * (`types.ts`) and back. Reads are total: anything the UI cannot show is
 * dropped. Writes start from the document the server returned (`base`) and
 * replace only the parts the UI owns, so fields the UI does not model
 * (resolutions, state SLA, triggers, slot actions, button properties) survive
 * a save.
 *
 * The document holds a subset of what the builder edits (see
 * `__mocks__/sidecar.ts`). For a step form or a transition's rules the
 * side-car value wins only while its projection still equals what the server
 * has; otherwise the server's value is shown.
 */
import { AutomationRuleMeta, VersionSidecar } from './__mocks__/sidecar';
import {
  ANY_STATE,
  ApprovalPolicyDto,
  ApproverRefDto,
  AutomationActionDto,
  AutomationConditionDto,
  AutomationDefinitionDto,
  BoardColumnDto,
  CreateWorkflowDefinitionRequestDto,
  CreateWorkflowDefinitionResultDto,
  FieldDefDto,
  FieldDefTypeDto,
  FieldOptionDto,
  InputRequestDto,
  RejectionPolicyDto,
  StateDefinitionDto,
  TransitionConditionDto,
  TransitionDefinitionDto,
  TransitionRulesContentDto,
  ValidateWorkflowDocumentResultDto,
  WorkflowBoardDto,
  WorkflowCreationModeDto,
  WorkflowDefinitionDetailsDto,
  WorkflowDefinitionDto,
  WorkflowDefinitionStateOptionDto,
  WorkflowDefinitionVersionDto,
  WorkflowDefinitionVersionListItemDto,
  WorkflowDefinitionVersionStateDto,
  WorkflowDocumentDto,
} from './dto';
import {
  ApprovalMode,
  ApprovalRule,
  Approver,
  ApproverType,
  AutomationAction,
  AutomationActionKind,
  AutomationAfterExecution,
  AutomationCondition,
  AutomationConditionKind,
  AutomationField,
  AutomationNotifyChannel,
  AutomationRecipient,
  AutomationRule,
  AutomationTrigger,
  AutomationTriggerKind,
  BoardLayout,
  BoardLayoutColumn,
  CreateWorkflowPayload,
  CreateWorkflowResponse,
  DraftStatus,
  DraftTransition,
  GlobalStatusCatalogItem,
  RejectionMode,
  RestrictionMode,
  StepForm,
  StepFormField,
  StepFormFieldType,
  TransitionRuleEdits,
  TransitionRules,
  Workflow,
  WorkflowAuthor,
  WorkflowDraftValidation,
  WorkflowStatus,
  WorkflowTransition,
  WorkflowVersion,
  WorkflowVersionListItem,
  WorkflowVersionOrigin,
  WorkflowVersionState,
  WorkflowVersionSummary,
} from './types';

/**
 * A version as the builder caches it: the model plus what a save needs — the
 * document to patch and the revision to send as `expectedRevision`.
 */
export interface MappedWorkflowVersion extends WorkflowVersion {
  documentRevision: number;
  document: WorkflowDocumentDto;
}

export interface MappedBoardLayout extends BoardLayout {
  revision: number;
}

// Role, department and employee references must be Guids (`ApproverIdInvalid`).
const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isGuid = (value: string) => GUID_PATTERN.test(value);

// --- Enums and shared fields ---

const VERSION_STATE: Record<
  WorkflowDefinitionVersionStateDto,
  WorkflowVersionState
> = {
  Draft: WorkflowVersionState.Draft,
  Published: WorkflowVersionState.Published,
  Retired: WorkflowVersionState.Retired,
};

export const toVersionOrigin = (
  mode: WorkflowCreationModeDto,
): WorkflowVersionOrigin =>
  mode === 'Agent' ? WorkflowVersionOrigin.Agent : WorkflowVersionOrigin.Human;

const toAuthor = (
  id: string | null | undefined,
  name: string | null | undefined,
): WorkflowAuthor | null => (id ? { id, name: name ?? '' } : null);

// --- Definitions and versions ---

/**
 * The list rows of the requested states, one per published or draft version.
 * The list carries no draft number: a draft is the version after the
 * published one, so it is derived.
 */
export const fromDefinitionList = (
  definitions: WorkflowDefinitionDto[],
  states: WorkflowVersionState[],
): WorkflowVersionListItem[] =>
  definitions.flatMap(definition => {
    const common = {
      workflowId: definition.id,
      name: definition.name ?? '',
      kind: definition.type,
      entityType: null,
      updatedAt: definition.updatedAt ?? definition.createdAt,
      updatedBy: toAuthor(definition.updatedBy, definition.updatedByName),
      updatedByOrigin: WorkflowVersionOrigin.Human,
      publishedAt: null,
    };
    const rows: WorkflowVersionListItem[] = [];

    if (
      states.includes(WorkflowVersionState.Published) &&
      definition.publishedVersionId
    ) {
      rows.push({
        ...common,
        versionId: definition.publishedVersionId,
        number: definition.publishedVersionNumber ?? 1,
        state: WorkflowVersionState.Published,
      });
    }

    if (
      states.includes(WorkflowVersionState.Draft) &&
      definition.draftVersionId
    ) {
      rows.push({
        ...common,
        versionId: definition.draftVersionId,
        number: (definition.publishedVersionNumber ?? 0) + 1,
        state: WorkflowVersionState.Draft,
      });
    }

    return rows;
  });

export const fromVersionSummary = (
  version: WorkflowDefinitionVersionListItemDto,
): WorkflowVersionSummary => ({
  id: version.id,
  number: version.number,
  state: VERSION_STATE[version.state],
  origin: toVersionOrigin(version.creationMode),
  createdAt: version.createdAt,
  publishedAt: version.publishedAt ?? null,
  updatedAt: version.updatedAt ?? version.createdAt,
  updatedBy: toAuthor(version.updatedBy, version.updatedByName),
});

/** The definition's own fields plus its versions, newest first. */
export const fromDefinitionDetails = (
  details: WorkflowDefinitionDetailsDto,
  versions: WorkflowDefinitionVersionListItemDto[],
): Workflow => ({
  id: details.id,
  name: details.name ?? '',
  description: details.description ?? '',
  kind: details.type,
  entityType: null,
  creationMode: details.creationMode,
  versions: versions
    .map(fromVersionSummary)
    .sort((left, right) => right.number - left.number),
});

export const fromVersion = (
  version: WorkflowDefinitionVersionDto,
  sidecar: VersionSidecar,
): MappedWorkflowVersion => ({
  ...fromVersionSummary(version),
  statuses: fromStates(version.document, sidecar),
  transitions: version.document.transitions.map(fromTransition),
  documentRevision: version.documentRevision,
  document: version.document,
});

/** The version after a save: its structure read again from the saved document. */
export const withDocument = (
  version: MappedWorkflowVersion,
  document: WorkflowDocumentDto,
  documentRevision: number,
  sidecar: VersionSidecar,
): MappedWorkflowVersion => ({
  ...version,
  statuses: fromStates(document, sidecar),
  transitions: document.transitions.map(fromTransition),
  documentRevision,
  document,
});

/**
 * The «Наявний статус» catalog. The service returns no key, so the name stands
 * in for one: it is unique in the list but is no valid document key — a status
 * copied from the catalog needs a key of its own.
 */
export const fromStatusOptions = (
  options: WorkflowDefinitionStateOptionDto[],
): GlobalStatusCatalogItem[] =>
  options.map(option => ({
    key: option.name ?? '',
    name: option.name ?? '',
    color: option.color ?? '',
    category: option.category,
    workflowNames: [],
  }));

export const toCreateDefinitionRequest = (
  payload: CreateWorkflowPayload,
): CreateWorkflowDefinitionRequestDto => ({
  name: payload.name,
  description: payload.description,
  type: payload.kind,
  creationMode: payload.creationMode,
});

export const fromCreateDefinitionResult = (
  result: CreateWorkflowDefinitionResultDto,
): CreateWorkflowResponse => ({
  id: result.id,
  versionId: result.draftVersionId,
});

// --- States and step forms ---

const FIELD_TYPE_TO_DTO: Record<StepFormFieldType, FieldDefTypeDto> = {
  Text: 'Text',
  Textarea: 'Textarea',
  Number: 'Number',
  Date: 'Date',
  Select: 'Select',
  PredefinedList: 'PredefinedList',
  Checkbox: 'Boolean',
  CheckboxList: 'CheckboxList',
  RadioList: 'RadioList',
  File: 'File',
  YesNo: 'YesNo',
};

const FIELD_TYPE_FROM_DTO: Record<FieldDefTypeDto, StepFormFieldType> = {
  Text: StepFormFieldType.Text,
  Textarea: StepFormFieldType.Textarea,
  Number: StepFormFieldType.Number,
  Decimal: StepFormFieldType.Number,
  Date: StepFormFieldType.Date,
  Boolean: StepFormFieldType.Checkbox,
  YesNo: StepFormFieldType.YesNo,
  Select: StepFormFieldType.Select,
  CheckboxList: StepFormFieldType.CheckboxList,
  RadioList: StepFormFieldType.RadioList,
  PredefinedList: StepFormFieldType.PredefinedList,
  File: StepFormFieldType.File,
};

// The types `FieldOptionsMissing` / `FieldOptionsNotAllowed` check.
const OPTION_TYPES = new Set<FieldDefTypeDto>([
  'Select',
  'CheckboxList',
  'RadioList',
]);

type FieldDefs = ReadonlyMap<string, FieldDefDto>;

const fieldDefsOf = (document: WorkflowDocumentDto): FieldDefs =>
  new Map((document.fields ?? []).map(field => [field.key, field]));

const optionsOf = (
  type: FieldDefTypeDto,
  options: FieldOptionDto[] | null | undefined,
): FieldOptionDto[] =>
  OPTION_TYPES.has(type)
    ? (options ?? []).map(option => ({
        value: option.value,
        label: option.label,
      }))
    : [];

const dictionaryOf = (
  type: FieldDefTypeDto,
  dictionaryId: string | null | undefined,
): string | null => (type === 'PredefinedList' ? (dictionaryId ?? null) : null);

/** The document field a step-form field stands for; `kept` keeps what the builder does not edit. */
const toFieldDef = (field: StepFormField, kept?: FieldDefDto): FieldDefDto => {
  const type = FIELD_TYPE_TO_DTO[field.type];
  return {
    ...kept,
    key: field.key,
    name: field.label,
    type,
    options: optionsOf(type, field.options),
    dictionaryId: dictionaryOf(type, field.dictionaryId),
    editableInStates: kept?.editableInStates ?? [],
    visibleToRoles: [...field.visibleToRoleIds],
  };
};

/**
 * A step form as the document holds it — its references resolved against
 * `document.fields` — in a comparable form: `null` when it asks for nothing.
 */
interface StepFormProjection {
  fields: {
    key: string;
    name: string;
    type: FieldDefTypeDto;
    required: boolean;
    options: FieldOptionDto[];
    dictionaryId: string | null;
    visibleToRoles: string[];
  }[];
  resolutionRequired: boolean;
}

const projectField = (
  field: FieldDefDto,
  required: boolean,
): StepFormProjection['fields'][number] => ({
  key: field.key,
  name: field.name,
  type: field.type,
  required,
  options: optionsOf(field.type, field.options),
  dictionaryId: dictionaryOf(field.type, field.dictionaryId),
  visibleToRoles: [...(field.visibleToRoles ?? [])],
});

const projection = (
  fields: StepFormProjection['fields'],
  resolutionRequired: boolean,
): StepFormProjection | null =>
  fields.length || resolutionRequired ? { fields, resolutionRequired } : null;

/** A reference to a field the document lacks is dropped. */
const projectServerStepForm = (
  stepForm: InputRequestDto | null | undefined,
  fields: FieldDefs,
): StepFormProjection | null =>
  projection(
    (stepForm?.fields ?? []).flatMap(ref => {
      const field = fields.get(ref.fieldKey);
      return field ? [projectField(field, ref.required)] : [];
    }),
    stepForm?.resolutionRequired ?? false,
  );

const projectStepForm = (
  stepForm: StepForm,
  resolutionRequired: boolean,
): StepFormProjection | null =>
  projection(
    stepForm.fields.map(field =>
      projectField(toFieldDef(field), field.isRequired),
    ),
    resolutionRequired,
  );

/**
 * The state's step form: references to `document.fields` (`toDocumentFields`
 * writes the definitions). `resolutionRequired` is not edited in the builder;
 * a save passes the stored one.
 */
export const toStepFormDto = (
  stepForm: StepForm | null | undefined,
  resolutionRequired = false,
): InputRequestDto | null => {
  const fields = (stepForm?.fields ?? []).map(field => ({
    fieldKey: field.key,
    required: field.isRequired,
  }));

  return fields.length || resolutionRequired
    ? { fields, resolutionRequired }
    : null;
};

const fromStepFormDto = (
  stepForm: InputRequestDto | null | undefined,
  fields: FieldDefs,
): StepForm | null => {
  const formFields = (stepForm?.fields ?? []).flatMap(
    (ref): StepFormField[] => {
      const field = fields.get(ref.fieldKey);
      if (!field) return [];

      const options = optionsOf(field.type, field.options);
      return [
        {
          id: field.key,
          key: field.key,
          label: field.name,
          type: FIELD_TYPE_FROM_DTO[field.type],
          isRequired: ref.required,
          filledByRoleId: null,
          visibleToRoleIds: [...(field.visibleToRoles ?? [])],
          ...(OPTION_TYPES.has(field.type) ? { options } : {}),
          ...(field.type === 'PredefinedList'
            ? { dictionaryId: field.dictionaryId ?? null }
            : {}),
        },
      ];
    },
  );

  return formFields.length ? { fields: formFields } : null;
};

const sameJson = (left: unknown, right: unknown) =>
  JSON.stringify(left) === JSON.stringify(right);

const resolveStepForm = (
  state: StateDefinitionDto,
  fields: FieldDefs,
  sidecar: VersionSidecar,
): StepForm | null => {
  const server = projectServerStepForm(state.stepForm, fields);
  const local = sidecar.stepForms[state.key];

  return local &&
    sameJson(
      projectStepForm(local, server?.resolutionRequired ?? false),
      server,
    )
    ? local
    : fromStepFormDto(state.stepForm, fields);
};

/** The field keys the document refers to outside its step forms. */
const fieldKeysReferencedOutsideStepForms = (
  document: WorkflowDocumentDto,
): Set<string> =>
  new Set(
    [
      ...document.transitions.flatMap(transition => [
        ...(transition.rules?.inputRequest?.fields ?? []).map(
          ref => ref.fieldKey,
        ),
        ...(transition.rules?.validators ?? []).map(
          validator => validator.fieldKey,
        ),
      ]),
      ...document.automations.flatMap(automation =>
        (automation.conditions ?? []).map(condition => condition.fieldKey),
      ),
    ].filter((key): key is string => !!key),
  );

/**
 * `document.fields` after a structure edit. Every step-form field is defined
 * once — by the first status that asks for its key — and keeps what the
 * builder does not edit (`editableInStates`). A field of `base` stays unless
 * only `base`'s step forms used it and none uses it now: the cases model, input
 * requests, validators and automations may still refer to it.
 */
const toDocumentFields = (
  base: WorkflowDocumentDto,
  statuses: DraftStatus[],
): FieldDefDto[] => {
  const kept = fieldDefsOf(base);
  const owned = new Map<string, FieldDefDto>();

  statuses.forEach(status =>
    status.stepForm?.fields.forEach(field => {
      if (!owned.has(field.key)) {
        owned.set(field.key, toFieldDef(field, kept.get(field.key)));
      }
    }),
  );

  const usedByBaseStepForms = new Set(
    base.states.flatMap(state =>
      (state.stepForm?.fields ?? []).map(ref => ref.fieldKey),
    ),
  );
  const usedElsewhere = fieldKeysReferencedOutsideStepForms(base);

  const others = (base.fields ?? []).filter(
    field =>
      !owned.has(field.key) &&
      (!usedByBaseStepForms.has(field.key) || usedElsewhere.has(field.key)),
  );

  return [...owned.values(), ...others];
};

// A status's `id` only has to be stable within one read, so the key serves.
const fromState = (
  state: StateDefinitionDto,
  order: number,
  fields: FieldDefs,
  sidecar: VersionSidecar,
): WorkflowStatus => ({
  id: state.key,
  key: state.key,
  name: state.name,
  color: state.color ?? '',
  order,
  category: state.category,
  isInitial: state.isInitial,
  isFinal: state.isFinal,
  stepForm: resolveStepForm(state, fields, sidecar),
});

const fromStates = (
  document: WorkflowDocumentDto,
  sidecar: VersionSidecar,
): WorkflowStatus[] => {
  const fields = fieldDefsOf(document);
  return document.states.map((state, order) =>
    fromState(state, order, fields, sidecar),
  );
};

// --- Transitions ---

/** A multi-source transition shows its first source; the rest is kept on save. */
const fromTransition = (
  transition: TransitionDefinitionDto,
): WorkflowTransition => ({
  id: transition.key,
  key: transition.key,
  name: transition.name,
  fromStatusKey: transition.from.includes(ANY_STATE)
    ? null
    : (transition.from[0] ?? null),
  toStatusKey: transition.to,
});

/**
 * The sources to save: the edited one, or the stored list when the edit left
 * its first source as it was (minus states the structure no longer has).
 */
const toTransitionFrom = (
  transition: DraftTransition,
  base: TransitionDefinitionDto | undefined,
  stateKeys: Set<string>,
): string[] => {
  if (transition.fromStatusKey === null) return [ANY_STATE];

  const kept = base?.from.filter(key => stateKeys.has(key)) ?? [];
  return kept.length > 1 && kept[0] === transition.fromStatusKey
    ? kept
    : [transition.fromStatusKey];
};

// --- Transition rules ---

/** What a rules projection needs to know about its transition. */
interface RulesContext {
  fromStatusKey: string | null;
  /** Where «Вийти з процесу» sends a rejected case. */
  exitStateKey: string | null;
}

const rulesContextOf = (
  document: WorkflowDocumentDto,
  transition: { from: string[] },
): RulesContext => ({
  fromStatusKey: transition.from.includes(ANY_STATE)
    ? null
    : (transition.from[0] ?? null),
  exitStateKey:
    document.states.find(
      state => state.isFinal && state.category === 'Rejected',
    )?.key ?? null,
});

/** The rule sections the builder owns: the server's or the projected ones. */
interface OwnedRules {
  approval: ApprovalPolicyDto | null;
  rejection: RejectionPolicyDto | null;
  conditions: TransitionConditionDto[];
}

const canonicalRef = (ref: ApproverRefDto): ApproverRefDto => ({
  kind: ref.kind,
  value: ref.value ?? null,
});

const canonicalOwnedRules = (
  rules: TransitionRulesContentDto | null | undefined,
): OwnedRules => ({
  approval: rules?.approval
    ? {
        approvers: (rules.approval.approvers ?? []).map(canonicalRef),
        rule: rules.approval.rule,
        requiredCount: rules.approval.requiredCount ?? null,
        finalCheckByLast: rules.approval.finalCheckByLast,
      }
    : null,
  rejection: rules?.rejection
    ? {
        target: rules.rejection.target,
        stateKey: rules.rejection.stateKey ?? null,
        reasonRequired: rules.rejection.reasonRequired,
      }
    : null,
  conditions: (rules?.conditions ?? []).map(condition => ({
    kind: condition.kind,
    refs: (condition.refs ?? []).map(canonicalRef),
    expression: condition.expression ?? null,
  })),
});

/**
 * The part of the builder's rules the MVP document accepts: an approval whose
 * approvers are all employees (Guid ids), the rejection target, and the
 * «approvers only» restriction. Everything else lives in the side-car.
 */
export const toOwnedRules = (
  rules: TransitionRules,
  context: RulesContext,
): OwnedRules => {
  const approvers = rules.approval?.approvers ?? [];
  const approval =
    rules.approval &&
    approvers.length > 0 &&
    approvers.every(
      approver => approver.type === ApproverType.User && isGuid(approver.id),
    )
      ? {
          approvers: approvers.map(approver => ({
            kind: 'Employee' as const,
            value: approver.id,
          })),
          rule: rules.approval.mode,
          requiredCount: null,
          finalCheckByLast: rules.approval.finalCheckByLastApprover,
        }
      : null;

  const stateKey =
    rules.rejection?.mode === RejectionMode.ReturnToStatus
      ? context.fromStatusKey
      : context.exitStateKey;
  const rejection =
    rules.rejection && stateKey
      ? {
          target:
            rules.rejection.mode === RejectionMode.ReturnToStatus
              ? ('ReturnToStatus' as const)
              : ('ExitToStatus' as const),
          stateKey,
          reasonRequired: false,
        }
      : null;

  const conditions =
    rules.restrictions?.mode === RestrictionMode.ApproversOnly
      ? [{ kind: 'OnlyApprovers' as const, refs: [], expression: null }]
      : [];

  return { approval, rejection, conditions };
};

const APPROVER_TYPE: Partial<Record<ApproverRefDto['kind'], ApproverType>> = {
  Employee: ApproverType.User,
  Role: ApproverType.Role,
  Department: ApproverType.Department,
};

// The document carries ids only; the side-car keeps the names the picker chose.
const fromApprovalDto = (
  approval: ApprovalPolicyDto | null,
): ApprovalRule | null =>
  approval
    ? {
        approvers: (approval.approvers ?? []).flatMap((ref): Approver[] => {
          const type = APPROVER_TYPE[ref.kind];
          return type && ref.value
            ? [{ type, id: ref.value, name: ref.value }]
            : [];
        }),
        mode: approval.rule === 'Any' ? ApprovalMode.Any : ApprovalMode.All,
        finalCheckByLastApprover: approval.finalCheckByLast,
      }
    : null;

const fromOwnedRules = (owned: OwnedRules): TransitionRules => ({
  approval: fromApprovalDto(owned.approval),
  rejection: owned.rejection
    ? {
        mode:
          owned.rejection.target === 'ReturnToStatus'
            ? RejectionMode.ReturnToStatus
            : RejectionMode.ExitProcess,
      }
    : null,
  restrictions: owned.conditions.some(
    condition => condition.kind === 'OnlyApprovers',
  )
    ? { mode: RestrictionMode.ApproversOnly }
    : null,
  actions: null,
  validators: null,
  input: null,
});

const findTransition = (document: WorkflowDocumentDto, transitionKey: string) =>
  document.transitions.find(transition => transition.key === transitionKey);

/** The rules the builder shows for one transition of the version. */
export const fromTransitionRules = (
  document: WorkflowDocumentDto,
  transitionKey: string,
  sidecar: VersionSidecar,
): TransitionRules => {
  const transition = findTransition(document, transitionKey);
  const server = canonicalOwnedRules(transition?.rules);
  const local = sidecar.transitionRules[transitionKey];

  if (
    transition &&
    local &&
    sameJson(toOwnedRules(local, rulesContextOf(document, transition)), server)
  ) {
    return local;
  }

  return fromOwnedRules(server);
};

const withOwnedRules = (
  rules: TransitionRulesContentDto | null | undefined,
  owned: OwnedRules,
): TransitionRulesContentDto => ({ ...rules, ...owned });

// --- Structure ---

/**
 * Replaces the states and transitions with the edited lists. A kept state
 * keeps its resolutions (while still final) and SLA; a kept transition keeps
 * its rules, and when its source moved the rejection target is projected
 * again, since «повернути» always means the current source. `transitionRules`
 * are the rules edited along with the structure, keyed by the transition's
 * `key` — a transition added in the same edit gets them too.
 */
export const applyStructure = (
  base: WorkflowDocumentDto,
  statuses: DraftStatus[],
  transitions: DraftTransition[],
  sidecar: VersionSidecar,
  transitionRules: TransitionRuleEdits = {},
): { document: WorkflowDocumentDto; sidecar: Partial<VersionSidecar> } => {
  const baseStates = new Map(base.states.map(state => [state.key, state]));
  const baseTransitions = new Map(
    base.transitions.map(transition => [transition.key, transition]),
  );
  const stateKeys = new Set(statuses.map(status => status.key));

  const states = statuses.map((status): StateDefinitionDto => {
    const kept = baseStates.get(status.key);
    return {
      ...kept,
      key: status.key,
      name: status.name,
      category: status.category,
      color: status.color,
      isInitial: status.isInitial,
      isFinal: status.isFinal,
      resolutions: status.isFinal ? (kept?.resolutions ?? []) : [],
      stepForm: toStepFormDto(
        status.stepForm,
        kept?.stepForm?.resolutionRequired ?? false,
      ),
    };
  });

  const document: WorkflowDocumentDto = {
    ...base,
    states,
    transitions: [],
    fields: toDocumentFields(base, statuses),
  };

  document.transitions = transitions.map(
    (transition): TransitionDefinitionDto => {
      const kept = baseTransitions.get(transition.key);
      const from = toTransitionFrom(transition, kept, stateKeys);
      const moved =
        kept &&
        (!sameJson(kept.from, from) || kept.to !== transition.toStatusKey);
      const projected =
        transitionRules[transition.key] ??
        (moved ? fromTransitionRules(base, transition.key, sidecar) : null);

      return {
        ...kept,
        key: transition.key,
        name: transition.name,
        from,
        to: transition.toStatusKey,
        rules: projected
          ? withOwnedRules(
              kept?.rules,
              toOwnedRules(projected, rulesContextOf(document, { from })),
            )
          : (kept?.rules ?? {}),
      };
    },
  );

  const stepForms = Object.fromEntries(
    statuses.flatMap(status =>
      status.stepForm?.fields.length ? [[status.key, status.stepForm]] : [],
    ),
  );

  const editedRules = Object.fromEntries(
    transitions.flatMap(transition =>
      transitionRules[transition.key]
        ? [[transition.key, transitionRules[transition.key]]]
        : [],
    ),
  );

  return {
    document,
    sidecar: { stepForms, transitionRules: editedRules },
  };
};

// --- Automations ---

const AUTOMATION_CONDITION_LIST_KINDS = new Set<string>([
  AutomationConditionKind.PreviousStatusIn,
  AutomationConditionKind.PreviousStatusNotIn,
  AutomationConditionKind.TransitionIs,
]);

const toAutomationTrigger = (
  trigger: AutomationTrigger,
): AutomationDefinitionDto['trigger'] =>
  trigger.kind === AutomationTriggerKind.TransitionExecuted
    ? {
        kind: trigger.kind,
        keys: trigger.transitionKey ? [trigger.transitionKey] : [],
      }
    : { kind: trigger.kind, keys: [trigger.statusKey] };

const toAutomationCondition = (
  condition: AutomationCondition,
): AutomationConditionDto =>
  condition.kind === AutomationConditionKind.StepFormValueEquals
    ? {
        kind: condition.kind,
        stateKey: condition.statusKey,
        fieldKey: condition.fieldKey,
        value: condition.value,
      }
    : { kind: condition.kind, keys: condition.keys };

const toAutomationAction = (action: AutomationAction): AutomationActionDto => {
  const afterExecution = { afterExecution: action.afterExecution };

  switch (action.kind) {
    case AutomationActionKind.MoveToStatus:
      return {
        kind: action.kind,
        targetStateKey: action.statusKey,
        parameters: afterExecution,
      };
    case AutomationActionKind.SendEmail:
      return {
        kind: action.kind,
        parameters: {
          ...afterExecution,
          recipient: action.recipient,
          templateId: action.templateId,
        },
      };
    case AutomationActionKind.Notify:
      return {
        kind: action.kind,
        parameters: {
          ...afterExecution,
          channel: action.channel,
          recipient: action.recipient,
        },
      };
    case AutomationActionKind.UpdateField:
      return {
        kind: action.kind,
        parameters: { ...afterExecution, field: action.field },
      };
  }
};

// The document stores dates only (`DateOnly`).
const toDateOnly = (value: string | null) =>
  value ? value.slice(0, 10) : null;

export const toAutomationDtos = (
  rules: AutomationRule[],
): AutomationDefinitionDto[] =>
  rules.map(rule => ({
    key: rule.id,
    isEnabled: rule.isEnabled,
    activeFrom: toDateOnly(rule.activeFrom),
    activeTo: toDateOnly(rule.activeTo),
    trigger: toAutomationTrigger(rule.trigger),
    conditions: rule.conditions.map(toAutomationCondition),
    actions: rule.actions.map(toAutomationAction),
  }));

const fromAutomationTrigger = (
  trigger: AutomationDefinitionDto['trigger'],
): AutomationTrigger =>
  trigger.kind === 'TransitionExecuted'
    ? {
        kind: AutomationTriggerKind.TransitionExecuted,
        transitionKey: trigger.keys?.[0] ?? null,
      }
    : {
        kind: AutomationTriggerKind.StatusEntered,
        statusKey: trigger.keys?.[0] ?? '',
      };

const fromAutomationCondition = (
  condition: AutomationConditionDto,
  id: string,
): AutomationCondition =>
  AUTOMATION_CONDITION_LIST_KINDS.has(condition.kind)
    ? {
        id,
        kind: condition.kind as Exclude<
          AutomationCondition['kind'],
          typeof AutomationConditionKind.StepFormValueEquals
        >,
        keys: condition.keys ?? [],
      }
    : {
        id,
        kind: AutomationConditionKind.StepFormValueEquals,
        statusKey: condition.stateKey ?? '',
        fieldKey: condition.fieldKey ?? '',
        value: condition.value ?? '',
      };

const fromAutomationAction = (
  action: AutomationActionDto,
  id: string,
): AutomationAction => {
  const parameters = action.parameters ?? {};
  const base = {
    id,
    afterExecution:
      parameters.afterExecution === AutomationAfterExecution.StopOtherRules
        ? AutomationAfterExecution.StopOtherRules
        : AutomationAfterExecution.None,
  };
  const recipient =
    parameters.recipient === AutomationRecipient.Assignee
      ? AutomationRecipient.Assignee
      : AutomationRecipient.CaseManager;

  switch (action.kind) {
    case 'MoveToStatus':
      return {
        ...base,
        kind: AutomationActionKind.MoveToStatus,
        statusKey: action.targetStateKey ?? '',
      };
    case 'SendEmail':
      return {
        ...base,
        kind: AutomationActionKind.SendEmail,
        recipient,
        templateId: parameters.templateId ?? '',
      };
    case 'Notify':
      return {
        ...base,
        kind: AutomationActionKind.Notify,
        channel:
          parameters.channel === AutomationNotifyChannel.Teams
            ? AutomationNotifyChannel.Teams
            : AutomationNotifyChannel.InApp,
        recipient,
      };
    case 'UpdateField':
      return {
        ...base,
        kind: AutomationActionKind.UpdateField,
        field: AutomationField.Assignee,
      };
  }
};

/**
 * The version's rules in run order. Who wrote a rule comes from the side-car;
 * the ids of conditions and actions only have to be stable within one read.
 */
export const fromAutomations = (
  document: WorkflowDocumentDto,
  meta: Record<string, AutomationRuleMeta>,
  fallbackCreatedAt: string,
): AutomationRule[] =>
  document.automations.map(automation => ({
    id: automation.key,
    isEnabled: automation.isEnabled,
    origin: meta[automation.key]?.origin ?? WorkflowVersionOrigin.Human,
    createdBy: meta[automation.key]?.createdBy ?? null,
    createdAt: meta[automation.key]?.createdAt ?? fallbackCreatedAt,
    activeFrom: automation.activeFrom ?? null,
    activeTo: automation.activeTo ?? null,
    trigger: fromAutomationTrigger(automation.trigger),
    conditions: (automation.conditions ?? []).map((condition, index) =>
      fromAutomationCondition(condition, `${automation.key}-c${index}`),
    ),
    actions: (automation.actions ?? []).map((action, index) =>
      fromAutomationAction(action, `${automation.key}-a${index}`),
    ),
  }));

// --- Board ---

export const fromBoard = (board: WorkflowBoardDto): MappedBoardLayout => ({
  columns:
    board.columns?.map(
      (column): BoardLayoutColumn => ({
        id: column.key,
        title: column.title,
        ...(column.color ? { color: column.color } : {}),
        statusKeys: column.stateKeys,
      }),
    ) ?? null,
  revision: board.revision,
});

export const toBoardColumns = (
  columns: BoardLayoutColumn[],
): BoardColumnDto[] =>
  columns.map(column => ({
    key: column.id,
    title: column.title,
    color: column.color ?? null,
    stateKeys: column.statusKeys,
  }));

// --- Validation ---

export const fromValidation = (
  result: ValidateWorkflowDocumentResultDto,
): WorkflowDraftValidation => ({
  canSaveDraft: result.canSaveDraft,
  canPublish: result.canPublish,
  errors: (result.errors ?? []).map(error => ({
    path: error.path ?? '',
    code: error.code ?? '',
    message: error.message ?? '',
    hint: error.hint ?? '',
    blocksDraftSave: error.blocksDraftSave,
  })),
});
